import { useSyncExternalStore } from "react";
import { COMPACT_MEDIA_QUERY_LIST } from "./compact.ts";
import { measureTextScale } from "./text-scale.ts";

/**
 * Compact mode as one attribute on `html`, fed by two signals.
 *
 * The first signal is the media query in compact.ts. It is true when the
 * screen is small or the browser default font size is large. The second is
 * the rendered text scale from text-scale.ts. It is above 1 when the host
 * enlarges text without a change to the default font size, which the media
 * query cannot see. Either signal puts `data-compact` on `html`. Every
 * compact style selects on that attribute, so the layout has one switch.
 *
 * Both signals apply one rule: compact mode starts when 20rem of rendered
 * text no longer fits the viewport. The media query states it in `rem`.
 * The zoom signal states it as `zoom * 320px > viewport width`, which is
 * 1.22 on a 390px phone and 1.28 on a 411px phone. A zoom below that has
 * the room the default layout gives it, as the same phone at the same
 * default font size would.
 *
 * `--text-zoom` on `html` carries the measured scale whenever it differs
 * from 1. Compact px sizes divide by it, so the rendered size stays at the
 * compact value whatever the host multiplies afterwards. The variable is
 * absent at 1, so the default layout does not change.
 *
 * See docs/adr/0015-compact-card-grid-for-large-text.md, amendment 1.
 */

export const COMPACT_ATTRIBUTE = "data-compact";
export const TEXT_ZOOM_PROPERTY = "--text-zoom";

/** 20rem at the 16px default, the width the media query tests. */
export const COMPACT_MIN_WIDTH_PX = 320;

/**
 * The probe rounds to two decimals and a font fallback can add a percent
 * or two. A scale this close to 1 is treated as 1.
 */
const TEXT_ZOOM_NOISE = 0.05;

type Listener = () => void;

type MediaQueryChange = { matches: boolean };

/** The part of `MediaQueryList` compact mode uses. */
export type CompactMediaQuery = {
  matches: boolean;
  addEventListener(
    type: "change",
    listener: (event: MediaQueryChange) => void,
  ): void;
  removeEventListener(
    type: "change",
    listener: (event: MediaQueryChange) => void,
  ): void;
};

/** The part of `Window` compact mode uses. A test passes a plain object. */
export type CompactModeHost = {
  innerWidth: number;
  matchMedia?: (query: string) => CompactMediaQuery;
  addEventListener?: (type: "resize", listener: () => void) => void;
  removeEventListener?: (type: "resize", listener: () => void) => void;
};

let mediaMatches = false;
let textZoom = 1;
let host: CompactModeHost | null = null;
let root: HTMLElement | null = null;
const listeners = new Set<Listener>();

function hasTextZoom() {
  return Math.abs(textZoom - 1) >= TEXT_ZOOM_NOISE;
}

/** Whether 20rem of text at the rendered zoom is wider than the viewport. */
export function zoomNeedsCompact(zoom: number, viewportWidthPx: number) {
  return zoom * COMPACT_MIN_WIDTH_PX > viewportWidthPx;
}

function isCompact() {
  if (mediaMatches) return true;
  // Below 320px the media query is already true, so a zoom of 1 or less
  // never reaches this branch with a true result.
  return host ? zoomNeedsCompact(textZoom, host.innerWidth) : false;
}

let applied = { compact: false, zoom: "" };

function apply() {
  if (!root) return;

  const next = {
    compact: isCompact(),
    zoom: hasTextZoom() ? String(textZoom) : "",
  };
  // A resize fires many times. Write the DOM and notify on a change only.
  if (next.compact === applied.compact && next.zoom === applied.zoom) return;
  applied = next;

  if (next.compact) {
    root.setAttribute(COMPACT_ATTRIBUTE, "");
  } else {
    root.removeAttribute(COMPACT_ATTRIBUTE);
  }

  if (next.zoom) {
    root.style.setProperty(TEXT_ZOOM_PROPERTY, next.zoom);
  } else {
    root.style.removeProperty(TEXT_ZOOM_PROPERTY);
  }

  for (const listener of listeners) listener();
}

/**
 * Evaluate the media query now and follow its changes. Call this once
 * before the first render, so the attribute is set before any grid mounts.
 * Returns a function that stops following the query.
 */
export function startCompactMode(win: CompactModeHost, doc: Document) {
  host = win;
  root = doc.documentElement;

  const media = win.matchMedia?.(COMPACT_MEDIA_QUERY_LIST);
  mediaMatches = media?.matches ?? false;
  apply();

  const onChange = (event: MediaQueryChange) => {
    mediaMatches = event.matches;
    apply();
  };
  media?.addEventListener("change", onChange);
  // The zoom rule reads the viewport width, so a rotation re-evaluates it.
  win.addEventListener?.("resize", apply);

  return () => {
    media?.removeEventListener("change", onChange);
    win.removeEventListener?.("resize", apply);
  };
}

/** Record a measured text scale. 1 or an unmeasurable null clears it. */
export function setTextZoom(scale: number | null) {
  textZoom = scale ?? 1;
  apply();
}

/** Measure the rendered text scale and record it. */
export function measureTextZoom(doc: Document) {
  setTextZoom(measureTextScale(doc));
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether compact mode is on, from either signal. */
export function useIsCompact() {
  return useSyncExternalStore(subscribe, isCompact, () => false);
}
